import { faLink, faPlay, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MultiSelect } from "react-multi-select-component";
import { Breadcrumb, Loading } from "../components/ui";
import { countryOptions, classesOptions } from "../variables/countryOptions";

// Section header component
const SectionHeader = ({ title }) => (
  <div className="flex items-center gap-3 mb-4 pb-2 border-b border-base-content/10">
    <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
      {title}
    </h3>
  </div>
);

function Process() {
  const { processName } = useParams();
  const [loading, setLoading] = useState(false);
  const [inputs, setInputs] = useState([]);
  const [result, setResult] = useState(null);
  const [trademarkData, setTrademarkData] = useState(null);
  const [process_, setProcess] = useState({});
  const [inputValues, setInputValues] = useState({});
  const [inputValueDifferentiator, setInputValueDifferentiator] = useState("");
  const [differentiators, setDifferentiators] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [nice_classes, setNiceClasses] = useState([]);
  const [countries, setCountries] = useState([]);

  const host = process.env.REACT_APP_SERVER;

  const countryValueRenderer = (selected) => {
    return selected.length
      ? selected.map(({ label }) => `${label.split(",")[0].trim()} `).join("")
      : "Select countries...";
  };

  const industryValueRenderer = (selected) => {
    return selected.length
      ? selected.map(({ label }) => `${label.split(",")[0].trim()} `).join("")
      : "Select classes...";
  };

  const handleAddDifferentiator = (e) => {
    e.preventDefault();
    if (inputValueDifferentiator && !differentiators.includes(inputValueDifferentiator)) {
      setDifferentiators([...differentiators, inputValueDifferentiator]);
      setInputValueDifferentiator("");
    }
  };

  const handleRemoveDifferentiator = (item) => {
    setDifferentiators(differentiators.filter((d) => d !== item));
  };

  useEffect(() => {
    setLoading(true);
    const fetchProcessData = async () => {
      try {
        const response = await fetch(`${host}/api/processes/${processName}`);
        const data = await response.json();
        setProcess({ ...data.data });
        setInputs(data.data.inputs || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchProcessData();
  }, [processName, host]);

  const handleInputChange = (inputName, newValue) => {
    setInputValues({ ...inputValues, [inputName]: newValue });
  };

  const handleCreate = async () => {
    setLoading(true);
    setStartTime(Date.now());
    setResult(null);
    setTrademarkData(null);

    try {
      const requestData = { ...process_, ...inputValues };

      if (countries?.length > 0) {
        requestData.countries = countries.map((d) => d.value);
      }
      if (nice_classes?.length > 0) {
        requestData.nice_classes = nice_classes.map((d) => d.value);
      }
      if (inputs.some((input) => input.name === "differentiators")) {
        requestData.differentiators = differentiators;
      } else if (inputs.some((input) => input.name === "attributes")) {
        requestData.attributes = differentiators;
      }

      const response = await fetch(`${host}/api/process`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const data = await response.json();
        const result = JSON.parse(data.message);
        if (Object.keys(result).includes("nice_classes")) {
          setTrademarkData(result);
        } else {
          setResult(result);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setEndTime(Date.now());
      setLoading(false);
    }
  };

  if (loading && !process_.name) {
    return <Loading size="lg" message="Loading process..." fullScreen />;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Processes", href: "/processes" },
          { label: processName },
        ]}
        className="mb-4"
      />

      {/* Header Card with Name Tile */}
      <div className="bg-base-100 rounded-lg p-4 sm:p-5 mb-6 border border-base-content/10">
        {/* Name Tile */}
        <div className="flex justify-center mb-4">
          <div className="bg-base-200 border border-transparent hover:border-base-content/20 rounded-lg px-8 py-6 inline-block">
            <span className="font-[Oswald] font-semibold uppercase tracking-tight -skew-x-6 text-[#555] text-2xl block text-center">
              {process_.name}
            </span>
          </div>
        </div>

        {/* Description */}
        {process_.description && (
          <p className="text-base-content/60 text-sm leading-relaxed text-center max-w-2xl mx-auto">
            {process_.description}
          </p>
        )}

        {/* Example & Resources */}
        {(process_.example || process_.resources?.length > 0) && (
          <div className="mt-4 pt-4 border-t border-base-content/10 flex flex-wrap justify-center gap-6 text-sm">
            {process_.example && (
              <div>
                <span className="text-base-content/40">Example:</span>
                <span className="ml-2 text-base-content/70">{process_.example}</span>
              </div>
            )}
            {process_.resources?.length > 0 && (
              <div>
                <span className="text-base-content/40">Resources:</span>
                <span className="ml-2 text-base-content/70">{process_.resources.join(", ")}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Inputs Section */}
      {inputs?.length > 0 && (
        <div className="bg-base-100 rounded-lg p-4 border border-base-content/10 mb-6">
          <SectionHeader title="Inputs" />
          <div className="space-y-4">
            {inputs.map((input) => (
              <div key={input.id || input.name}>
                <label className="block text-sm font-medium text-base-content/70 mb-2">
                  {input.name}
                </label>

                {input.input_type === "text_area" && (
                  <textarea
                    placeholder={input.example?.replace('"', "")}
                    className="w-full px-3 py-2 text-sm bg-base-200 border border-base-content/10 rounded-lg focus:outline-none focus:border-primary/50 min-h-[100px]"
                    value={inputValues[input.name] || ""}
                    onChange={(e) => handleInputChange(input.name, e.target.value)}
                  />
                )}

                {input.input_type === "multi-select" && (
                  <MultiSelect
                    valueRenderer={input.name === "countries" ? countryValueRenderer : industryValueRenderer}
                    options={input.name === "countries" ? countryOptions : classesOptions}
                    value={input.name === "countries" ? countries : nice_classes}
                    onChange={input.name === "countries" ? setCountries : setNiceClasses}
                    labelledBy="Select..."
                    isCreatable={false}
                    hasSelectAll={false}
                    className="text-sm"
                  />
                )}

                {input.input_type === "number_input" && (
                  <input
                    type="number"
                    placeholder={input.example}
                    className="w-full px-3 py-2 text-sm bg-base-200 border border-base-content/10 rounded-lg focus:outline-none focus:border-primary/50"
                    value={inputValues[input.name] || ""}
                    onChange={(e) => handleInputChange(input.name, e.target.value)}
                  />
                )}

                {input.input_type === "list" && (
                  <div>
                    <form onSubmit={handleAddDifferentiator} className="flex gap-2">
                      <input
                        type="text"
                        value={inputValueDifferentiator}
                        onChange={(e) => setInputValueDifferentiator(e.target.value)}
                        placeholder="Add item..."
                        className="flex-1 px-3 py-2 text-sm bg-base-200 border border-base-content/10 rounded-lg focus:outline-none focus:border-primary/50"
                      />
                      <button
                        type="submit"
                        className="px-5 py-2.5 bg-primary text-primary-content rounded-full text-sm font-medium uppercase tracking-wider"
                      >
                        Add
                      </button>
                    </form>
                    {differentiators.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {differentiators.map((item, idx) => (
                          <span
                            key={idx}
                            onClick={() => handleRemoveDifferentiator(item)}
                            className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs cursor-pointer hover:bg-primary/20"
                          >
                            {item} ×
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Run Button */}
          <div className="mt-6 pt-4 border-t border-base-content/10">
            <button
              onClick={handleCreate}
              disabled={loading || Object.keys(inputValues).length === 0}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-content rounded-full text-sm font-medium uppercase tracking-wider disabled:opacity-50"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Running...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlay} className="text-xs" />
                  Run Process
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {(result || trademarkData) && (
        <div className="bg-base-100 rounded-lg p-4 border border-base-content/10">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-base-content/10">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
              Results
            </h3>
            {startTime && endTime && (
              <span className="text-xs text-base-content/40">
                {((endTime - startTime) / 1000).toFixed(2)}s
              </span>
            )}
          </div>

          {/* Trademark Results */}
          {trademarkData && (
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold border-2 ${
                  trademarkData.grade === "A" ? "bg-success/20 border-success text-success" :
                  trademarkData.grade === "B" ? "bg-info/20 border-info text-info" :
                  trademarkData.grade === "C" ? "bg-warning/20 border-warning text-warning" :
                  trademarkData.grade === "D" ? "bg-error/20 border-error text-error" :
                  "bg-error border-error text-white"
                }`}>
                  {trademarkData.grade}
                </div>
                <div>
                  <h4 className="font-[Oswald] font-semibold uppercase tracking-tight -skew-x-6 text-[#555] text-xl">
                    {trademarkData.name}
                  </h4>
                  <p className="text-xs text-base-content/50">{trademarkData.count} results found</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-base-content/50">Countries:</span>
                  <span className="ml-2 text-base-content">{trademarkData.countries?.join(", ")}</span>
                </div>
                <div>
                  <span className="text-base-content/50">Classes:</span>
                  <span className="ml-2 text-base-content">{trademarkData.nice_classes?.join(", ")}</span>
                </div>
              </div>

              {trademarkData.url && (
                <a
                  href={trademarkData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <FontAwesomeIcon icon={faLink} className="text-xs" />
                  View on TMView
                </a>
              )}
            </div>
          )}

          {/* Name Results */}
          {result && (
            <div className="flex flex-wrap gap-2">
              {Array.isArray(result) ? (
                result.map((name, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-2 bg-base-content/5 rounded-lg text-sm font-[Oswald] font-semibold uppercase tracking-tight -skew-x-6 text-[#555]"
                  >
                    {name}
                  </span>
                ))
              ) : (
                <pre className="text-sm text-base-content/70 whitespace-pre-wrap">
                  {JSON.stringify(result, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Process;
